/**
 * Article public base URL rules (path mode, Spec Issues #8/#9). Pure and import-free: imported by builder-service
 * RELATIVELY (deploy Guard 4) and by the frontend through @modules. No normalization except trimming the ends and
 * lower-casing the scheme and host — every other deviation is refused with an explicit reason.
 * The edge-gateway keeps a reader-side mirror (services/articleSiteSettings.ts) pinned to this file by
 * edge-gateway/src/__tests__/articleSiteSettings.parity.test.ts.
 * NOTE: not the publish-probe `publicBaseUrl` of cli.types (contradiction C-5).
 */
export const PUBLIC_BASE_URL_MAX_LENGTH = 2048;
export const SEGMENTLY_SERVED_HOSTS: readonly string[] = ['api.segmently.ai', 'dev-api.segmently.ai', 'stage-api.segmently.ai'];

export type PublicBaseUrlInvalidReason = 'empty' | 'too_long' | 'not_https' | 'trailing_slash' | 'query_or_fragment' | 'userinfo'
  | 'port' | 'ip_literal' | 'path_characters' | 'malformed' | 'same_as_article_host' | 'segmently_host';

export interface PublicBaseUrlContext { allowLocalHttp: boolean; articleHost?: string | null; segmentlyHosts?: readonly string[] }
export type PublicBaseUrlValidation = { ok: true; value: string } | { ok: false; reason: PublicBaseUrlInvalidReason; message: string };

export const PUBLIC_BASE_URL_MESSAGES: Record<PublicBaseUrlInvalidReason, string> = {
  empty: 'Enter the public URL of your blog, or use Disable to go back to the subdomain.',
  too_long: `The URL must be at most ${PUBLIC_BASE_URL_MAX_LENGTH} characters.`,
  not_https: 'The URL must start with https://',
  trailing_slash: 'Remove the trailing slash (for example https://www.example.com/blog).',
  query_or_fragment: 'The URL cannot contain ? or #.',
  userinfo: 'The URL cannot contain a user name or password.',
  port: 'The URL cannot contain a port.',
  ip_literal: 'Use a domain name, not an IP address.',
  path_characters: 'The path may contain only letters, numbers, ".", "_", "~", "-" and single "/" between segments.',
  malformed: 'This is not a valid URL.',
  same_as_article_host: 'This is your article domain itself. Enter the URL of the site that proxies it (for example https://www.example.com/blog).',
  segmently_host: 'This host is served by Segmently. Enter the URL of your own site.',
};

const SCHEME = /^([A-Za-z][A-Za-z0-9+.-]*):\/\/([\s\S]*)$/;
const HOST = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;
const IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/;
const SEGMENT = /^[A-Za-z0-9._~-]+$/;
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

const refuse = (reason: PublicBaseUrlInvalidReason): PublicBaseUrlValidation => ({ ok: false, reason, message: PUBLIC_BASE_URL_MESSAGES[reason] });

export function validatePublicBaseUrl(raw: string, context: PublicBaseUrlContext): PublicBaseUrlValidation {
  if (typeof raw !== 'string' || raw.trim() === '') return refuse('empty');
  const value = raw.trim();
  if (value.length > PUBLIC_BASE_URL_MAX_LENGTH) return refuse('too_long');
  const schemeMatch = SCHEME.exec(value);
  if (!schemeMatch) return refuse('malformed');
  const scheme = schemeMatch[1].toLowerCase();
  const rest = schemeMatch[2];
  if (rest.includes('?') || rest.includes('#')) return refuse('query_or_fragment');
  const slash = rest.indexOf('/');
  const authority = slash === -1 ? rest : rest.slice(0, slash);
  const path = slash === -1 ? '' : rest.slice(slash);
  if (authority.includes('@')) return refuse('userinfo');
  if (authority.startsWith('[')) return refuse('ip_literal');
  const [hostRaw, port, extra] = authority.split(':');
  if (extra !== undefined) return refuse('malformed');
  const host = (hostRaw ?? '').toLowerCase();
  const localHttp = context.allowLocalHttp && scheme === 'http' && LOCAL_HOSTS.has(host);
  if (scheme !== 'https' && !localHttp) return refuse('not_https');
  if (port !== undefined && !(localHttp && /^\d{1,5}$/.test(port))) return refuse('port');
  if (!localHttp && IPV4.test(host)) return refuse('ip_literal');
  if (!localHttp && !HOST.test(host)) return refuse('malformed');
  if (path.endsWith('/')) {
    if (path.slice(1, -1).split('/').some((segment) => segment === '')) return refuse(path === '/' ? 'trailing_slash' : 'path_characters');
    return refuse('trailing_slash');
  }
  if (path && path.slice(1).split('/').some((segment) => !SEGMENT.test(segment) || segment === '.' || segment === '..')) return refuse('path_characters');
  if (context.articleHost && host === context.articleHost.toLowerCase()) return refuse('same_as_article_host');
  const served = [...SEGMENTLY_SERVED_HOSTS, ...(context.segmentlyHosts ?? [])].map((h) => h.toLowerCase());
  if (served.includes(host)) return refuse('segmently_host');
  return { ok: true, value: `${scheme}://${host}${port !== undefined ? `:${port}` : ''}${path}` };
}
